import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Badge, PageLoader, TextButton } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useLocale } from '../../../../lib/locale-context';
import { useNarrow } from '../../../../lib/use-narrow';
import { useChatOverlayOpen } from '../../../../lib/use-chat-overlay';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { useMaestroBrief } from '../../../../lib/use-maestro-brief';
import { useAgentName } from '../../../../lib/use-agent-name';
import { getUnreadCount, subscribeUnreadCount } from '../../../../lib/unread-notifications-store';
import { useMapWorld } from '../../../../lib/home-map/use-map-world';
import { useMapLive } from '../../../../lib/home-map/use-map-live';
import { useMapHistorySources } from '../../../../lib/home-map/use-map-history-sources';
import { useContactBook } from '../../../../lib/contact-book';
import { channelSlot } from '../../../../lib/contact-rows';
import { useTasks } from '../../../../lib/tasks';
import { useMyAgents } from '../../../../lib/my-agents';
import { outreachNodeId } from '../../../../lib/home-map/map-layout';
import { calendarSlug } from '../../../../lib/home-map/channels';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../../../lib/query-client';
import { fetchConnectors } from '../../../../lib/connectors';
import { useAgentConnections } from '../../../../lib/agent-connections';
import { useRecipient, useWhatsappGroups } from '../../../settings/shared/settings-queries';
import { useMe } from '../../../../lib/me';
import { useOpenPhoneConnect } from '../../../../lib/phone-connect';
import { useSiteInfo } from '../../../../lib/site-info';
import { markChannelWanted, useOutreachPreferences } from '../../../../lib/outreach-preferences';
import { sendFeedback } from '../../../../lib/feedback';
import { WHATSAPP_DIRECT_URL } from '../../settings-whatsapp/constants';
import { connectorsPath } from '../../../../lib/connectors-route';
import { shortRelativeTime } from '../../../../lib/contact-rows';
import { continueOnChannelHref } from '../../../../lib/external-channel';
import { BRIEF_ID, MAESTRO_ID, spaceNodeId, type BucketId, type MapNode } from '../../../../lib/home-map/map-layout';
import type { MapEvent } from '../../../../lib/home-map/map-events';
import { AgentsIslandBadge, useAgentsPillReady } from '../../AgentsIslandBadge';
import { useChipSlotHolds } from '../../chip-morph/ChipSlotContext';
import { GreetingDate } from '../../DashboardView/GreetingDate';
import { HomeComposer } from '../../HomeComposer';
import { TasksBody } from '../../tasks/TasksBody';
import { GoalsBody } from '../../goals/GoalsBody';
import { RoutinesBody } from '../../routines/RoutinesBody';
import { AgentWorkspaceFilesBody } from '../../files/AgentWorkspaceFilesBody';
import { AppsBody } from '../../apps/AppsBody';
import { ActivityBody } from '../../activity/ActivityBody';
import { OutreachTabBody } from '../../contacts/OutreachTabBody';
import { HomeMap, type MapSelection } from './HomeMap';
import type { MapCardContent } from './MapInfoCard';
import { MapTimeline } from './MapTimeline';
import { MapBriefCard } from './MapBriefCard';
import { INLINE_TABS, MapTiles, TILE_ROUTES, type MapTab } from './MapTiles';
import styles from './MapHome.module.css';

/**
 * The map home (Home 2, `symphonyHome2Web`) — the one screen that replaces both the Home and Your Agents:
 * the header (island, greeting, date, the "N new updates" pill), the live map with its timeline, the six
 * entry tiles, and the Ask Maestro composer. See docs/claude/home2-map.md.
 *
 * Desktop: the tiles are TABS. Picking one shrinks the map to a band and renders that section beneath it —
 * the SAME `TasksBody` / `GoalsBody` / `RoutinesBody` / files explorer / apps grid / activity log the full
 * pages render — with the tab in the URL (`?tab=files`) so a refresh restores it, and a "Back to the map"
 * above the section. Two states only: LARGE (the map with its controls and the tiles under it) and SMALL
 * (a static band over the open section); any click on the small map brings the large one back. The phone
 * navigates instead (written-down web idiom; the phones push a screen).
 *
 * `landOn` is what the Agents destination differs by: the same screen, opened with the camera on the agents
 * ring instead of the brief.
 */
export function MapHome({ landOn = 'brief', showIsland = true }: { landOn?: 'brief' | 'agents'; showIsland?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();
  const narrow = useNarrow();
  const chatOpen = useChatOverlayOpen();
  const basePath = useBasePath();
  const agentName = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const { world, known, drawnAgentIds, agentNames, rosterReady } = useMapWorld();
  const sources = useMapHistorySources(known, drawnAgentIds);
  // Replies waiting on the reader, per outreach channel — the count on the channel's tile.
  const book = useContactBook('');
  const { badges, waiting } = useMemo(() => {
    const badges: Record<string, number> = {};
    // Who is waiting on which channel — what the channel's card lists (the count alone said nothing).
    const waiting: Record<string, { name: string; text: string; href?: string; at: number }[]> = {};
    if (book.kind !== 'ready') return { badges, waiting };
    for (const person of book.contacts) {
      for (const thread of person.inbound ?? []) {
        if (!thread.waitingOnMe) continue;
        const slot = channelSlot(thread.channel);
        const slug = slot === 'gmail_connector' ? 'email' : slot === 'app' || slot === 'other' ? null : slot;
        if (!slug) continue;
        badges[outreachNodeId(slug)] = (badges[outreachNodeId(slug)] ?? 0) + 1;
        const personId = person.contact?.personId;
        (waiting[slug] ??= []).push({
          name: person.contact?.displayName ?? '',
          text: thread.lastMessage ?? '',
          href: personId ? `/?tab=messages&person=${encodeURIComponent(personId)}` : undefined,
          at: thread.updatedAt ? new Date(thread.updatedAt).getTime() : 0,
        });
      }
    }
    for (const list of Object.values(waiting)) list.sort((a, b) => b.at - a.at);
    return { badges, waiting };
  }, [book]);
  const { state: taskState } = useTasks();
  const team = useMyAgents();
  // What the channel and connector cards say: the account's own facts, from the reads the app makes anyway.
  const connectorsQuery = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
  const connectorsList = connectorsQuery.data?.connectors ?? [];
  const { connections: coderConnections } = useAgentConnections();
  const recipient = useRecipient().data ?? null;
  const { groups } = useWhatsappGroups();
  const me = useMe();
  const openPhoneConnect = useOpenPhoneConnect();
  const site = useSiteInfo();
  const { prefs } = useOutreachPreferences();
  const [voted, setVoted] = useState<Record<string, boolean>>({});
  // "I want this": file the wish to the feedback channel and mark it on the account — no chat opens.
  const wantChannel = useCallback(
    (slug: string, label: string) => {
      logPageClickBi({ page_name: 'map_home', button_name: 'want-channel', answer: slug });
      setVoted((v) => ({ ...v, [slug]: true }));
      void markChannelWanted(slug).catch(() => setVoted((v) => ({ ...v, [slug]: false })));
      void sendFeedback({ feedback: t('connectors.channelsOut.wantSeed', { channel: label }), topic: `Outreach channel wanted: ${label}` }).catch(
        () => undefined,
      );
    },
    [logPageClickBi, t],
  );
  const agentsById = useMemo(() => Object.fromEntries(team.status === 'ready' ? team.team.agents.map((a) => [a.id, a]) : []), [team]);
  const live = useMapLive(known, drawnAgentIds, rosterReady, sources);
  useLoadBi('home-map-replay', { isLoading: live.replay === 'loading', isError: live.replay === 'error' });
  const brief = useMaestroBrief();
  const unread = useSyncExternalStore(subscribeUnreadCount, getUnreadCount, () => 0);
  const [bumped, setBumped] = useState<Partial<Record<BucketId, number>>>({});
  // A tick on the timeline: scrub to that moment AND open that event's card.
  const [picked, setPicked] = useState<{ event: MapEvent; seq: number } | null>(null);
  // The phone: the home carries a short live band; tapping it opens the full map over the screen.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!narrow) setExpanded(false);
  }, [narrow]);
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [expanded]);
  const onOpenSpace = useCallback(
    (node: MapNode) => {
      if (!node.href) return;
      logPageClickBi({ page_name: 'map_home', button_name: 'map-space', answer: node.id });
      void router.push(node.href, `${basePath}${node.href}`);
    },
    [router, basePath, logPageClickBi],
  );

  // The desktop tab rides the URL. Narrow has no tab state: a tile navigates.
  const tabParam = typeof router.query.tab === 'string' ? router.query.tab : '';
  const tab: MapTab | null = !narrow && (INLINE_TABS as readonly string[]).includes(tabParam) ? (tabParam as MapTab) : null;
  const setTab = useCallback(
    (next: MapTab | null) => {
      const query = { ...router.query };
      if (next) query.tab = next;
      else delete query.tab;
      // A person is a place INSIDE the Outreach tab; leaving that tab leaves the person too.
      if (next !== 'messages') delete query.person;
      // `as` carries the active mount prefix — the app runs with no basePath (see orion-web CLAUDE.md), so a
      // bare `{ pathname }` replace would drop `/orion-web` and land on the root mount.
      const qs = new URLSearchParams(Object.entries(query).flatMap(([k, v]) => (typeof v === 'string' ? [[k, v]] : []))).toString();
      const as = `${basePath}${router.pathname === '/' ? '/' : router.pathname}${qs ? `?${qs}` : ''}`;
      void router.replace({ pathname: router.pathname, query }, as, { shallow: true, scroll: false });
    },
    [router, basePath],
  );
  const onSelectTile = (bucket: BucketId) => setTab(tab === bucket ? null : bucket);
  // A person opened inside the Outreach tab rides the URL too (`?tab=messages&person=<id>`).
  const person = typeof router.query.person === 'string' ? router.query.person : '';
  const setPerson = useCallback(
    (id: string | null) => {
      const query: Record<string, string | string[] | undefined> = { ...router.query, tab: 'messages' };
      if (id) query.person = id;
      else delete query.person;
      const qs = new URLSearchParams(Object.entries(query).flatMap(([k, v]) => (typeof v === 'string' ? [[k, v]] : []))).toString();
      const as = `${basePath}${router.pathname === '/' ? '/' : router.pathname}${qs ? `?${qs}` : ''}`;
      void router.replace({ pathname: router.pathname, query }, as, { shallow: true, scroll: false });
    },
    [router, basePath],
  );
  // A tab set on a wide window must not survive into a narrow one as a hidden state.
  useEffect(() => {
    if (narrow && tabParam) setTab(null);
  }, [narrow, tabParam, setTab]);

  const onLand = useCallback((bucket: BucketId) => setBumped((prev) => ({ ...prev, [bucket]: Date.now() })), []);

  const onTimeline = useCallback(() => {
    logPageClickBi({ page_name: 'map_home', button_name: 'map-timeline' });
    if (narrow) void router.push('/activity', `${basePath}/activity`);
    else setTab(tab === 'activity' ? null : 'activity');
  }, [narrow, router, basePath, tab, setTab, logPageClickBi]);

  const timeOf = useCallback((at: number) => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(at)), [locale]);
  const onPick = useCallback(
    (event: MapEvent) => {
      logPageClickBi({ page_name: 'map_home', button_name: 'timeline-tick', answer: event.kind });
      live.scrub(event.at);
      setPicked((prev) => ({ event, seq: (prev?.seq ?? 0) + 1 }));
    },
    [live.scrub, logPageClickBi],
  );
  const timeline = (
    <MapTimeline
      history={live.history}
      mode={live.mode}
      playhead={live.playhead}
      onPlay={live.play}
      onPause={live.pause}
      onScrub={live.scrub}
      onLive={live.live}
      onPick={onPick}
    />
  );

  // What the info card says for each thing on the map — and what its ONE action is.
  const describe = useCallback(
    function describe(sel: MapSelection): MapCardContent | null {
      if (!world) return null;
      const node = (id: string) => world.nodes.find((n) => n.id === id);
      const nameOf = (n?: MapNode) =>
        n
          ? n.id === MAESTRO_ID
            ? agentName.short
            : n.id === BRIEF_ID
            ? t('home.map.brief')
            : n.region === 'buckets'
            ? t(`home.map.bucket.${n.label}`)
            : n.label
          : '';
      const last = (id: string) => live.lastByNode[id];
      // What a node last did, as a sentence: "Wrote a file: log.md" — a bare "log.md" said nothing.
      // The channel is the real one — the string carries it mid-sentence in some languages.
      const recentLine = (event?: MapEvent) => {
        if (!event) return undefined;
        const channel = nameOf(node(event.kind === 'message' ? event.from : event.to));
        return [t(`home.map.event.${event.kind}`, { channel }), event.text].filter(Boolean).join(': ');
      };
      const describeEvent = (event: MapEvent, region: string): MapCardContent => {
        // The card says WHAT HAPPENED in words — "Wrote a file", "Reached out on WhatsApp" — never
        // "Maestro → Personal": the arrow said where a dot flew, not what the agent did.
        // The actor: the agent that logged it, else the main agent — except the meeting, which is everyone's.
        // The actor: the agent that logged it. Unattributed rows name nobody — a reply that CAME IN is the
        // person's, the meeting is everyone's, and a file with no author is not the main agent's by default.
        const who = event.agentId ? agentNames[event.agentId] : undefined;
        const spaceName = event.workspaceId ? node(spaceNodeId(event.workspaceId))?.label : undefined;
        const channel = nameOf(node(event.kind === 'message' ? event.from : event.to));
        const title = t(`home.map.event.${event.kind}`, { channel });
        // Its action opens the THING: the file itself, the task, the goal, the person's thread, the chat.
        const label =
          event.kind === 'file'
            ? t('home.map.openFile')
            : event.kind === 'task' || event.relatedTaskId
            ? t('home.map.openTask')
            : event.kind === 'goal'
            ? t('home.map.openGoal')
            : event.kind === 'app'
            ? t('home.map.openApps')
            : event.kind === 'outreach' || event.kind === 'message'
            ? t('home.map.viewMessage')
            : t('home.map.view');
        const action = event.href
          ? { label, href: event.href }
          : event.relatedTaskId
          ? { label: t('home.map.openTask'), href: `/tasks/${event.relatedTaskId}` }
          : event.kind === 'brief' && brief
          ? { label: t('home.map.readMeeting'), onClick: () => brief.open() }
          : { label: t('home.map.timeline'), onClick: onTimeline };
        return {
          key: event.id,
          region,
          title,
          meta: [who, spaceName, timeOf(event.at)].filter(Boolean).join(' · '),
          text: event.text,
          action,
        };
      };
      if (sel.kind === 'event') return describeEvent(sel.event, t('home.map.eventRegion'));
      if (sel.kind === 'edge') {
        // A wire is the thing at its far end: its card, under the wire's own name.
        const a = node(sel.from);
        const b = node(sel.to);
        if (!a || !b) return null;
        const end = a.id === MAESTRO_ID ? b : a;
        const endCard = describe({ kind: 'node', id: end.id });
        if (!endCard) return null;
        return {
          ...endCard,
          key: `${sel.from}>${sel.to}`,
          region: t('home.map.wire'),
          title: `${nameOf(a)} → ${nameOf(b)}`,
          meta: endCard.meta ?? endCard.title,
        };
      }
      const n = node(sel.id);
      if (!n) return null;
      const recent = last(n.id);
      // A folded "+N" plate names what it hides; its action is the screen that lists them all.
      if (n.folded) {
        const region =
          n.region === 'connectors' ? t('home.map.connectors') : n.region === 'outreach' ? t('home.map.outreach') : t('home.map.channelsIn');
        const href =
          n.region === 'connectors'
            ? connectorsPath({ tab: 'tools' })
            : n.region === 'outreach'
            ? connectorsPath({ tab: 'channels-out' })
            : connectorsPath({ tab: 'channels' });
        return {
          key: n.id,
          region,
          title: t('home.map.moreHidden', { count: n.folded }),
          lines: n.hidden ?? [],
          action: { label: t('home.map.manage'), href },
        };
      }
      switch (n.region) {
        case 'centre':
          return {
            key: n.id,
            region: t('home.map.mainAgent'),
            title: agentName.short,
            meta: recent ? timeOf(recent.at) : undefined,
            text: recentLine(recent),
            action: { label: t('home.map.open'), href: '/agents/maestro' },
          };
        case 'brief':
          return {
            key: n.id,
            region: t('home.map.brief'),
            title: t('home.map.brief'),
            meta: brief ? undefined : t('home.map.quiet'),
            text: brief?.quote?.text ?? brief?.headline,
            action: brief ? { label: t('home.map.readMeeting'), onClick: () => brief.open() } : undefined,
          };
        case 'agents': {
          // The Your Agents card's facts: who they are, what they do, how much is on their desk.
          const id = n.id.slice('agent:'.length);
          const agent = agentsById[id];
          const open =
            taskState.kind === 'ready'
              ? taskState.tasks.filter(
                  (task) => task.agentId === id && task.status !== 'COMPLETED' && task.status !== 'FAILED' && task.status !== 'CANCELLED',
                ).length
              : 0;
          const meta = [agent?.title, open ? t('home.map.openTasks', { count: open }) : undefined, recent ? timeOf(recent.at) : undefined]
            .filter(Boolean)
            .join(' · ');
          return {
            key: n.id,
            region: t('home.map.agent'),
            title: n.label,
            meta: meta || undefined,
            text: recentLine(recent) || agent?.description || undefined,
            action: { label: t('home.map.open'), href: `/agents/${id}` },
            link: open ? { label: t('home.map.openTasks', { count: open }), href: `/tasks?agent=${encodeURIComponent(id)}` } : undefined,
          };
        }
        case 'buckets': {
          const bucket = n.label as BucketId;
          return {
            key: n.id,
            region: t('home.map.bucketRegion'),
            title: t(`home.map.bucket.${bucket}`),
            meta: recent ? timeOf(recent.at) : undefined,
            text: recent?.text,
            action: { label: t('home.map.open'), href: TILE_ROUTES[bucket] },
          };
        }
        case 'connectors':
          if (n.action === 'connect')
            return {
              key: n.id,
              region: t('home.map.connectors'),
              title: t('home.map.connectMore'),
              text: t('home.map.connectMoreText'),
              action: { label: t('home.map.connect'), href: '/settings/connectors' },
            };
          {
            // Which ACCOUNT the tool is connected as (the Gmail address, the LinkedIn handle); for Wix, the site.
            // A native calendar row has no toolkit: it is found by the slug the map gave it.
            const status = connectorsList.find((c) => (calendarSlug(c)?.slug ?? c.toolkit) === n.slug);
            const isCalendar = !!status && !!calendarSlug(status);
            const accounts = (status?.accounts ?? []).map((a) => a.label).filter((l): l is string => !!l);
            // Wix: the account's own site(s) — name and address — since the Symphony metasite is siteless.
            const sites = n.slug === 'wix' ? site?.sites ?? [] : [];
            const meta: string =
              n.slug === 'wix'
                ? sites[0]?.name || site?.siteDisplayName || site?.businessName || t('home.map.connected')
                : accounts.length
                ? accounts.join(' · ')
                : recent
                ? timeOf(recent.at)
                : t('home.map.connected');
            const siteUrl = sites[0]?.viewUrl ?? null;
            return {
              key: n.id,
              region: t('home.map.connectors'),
              title: n.label,
              meta,
              text: recent?.text,
              lines: sites.length > 1 ? sites.slice(1).map((s) => s.name) : siteUrl ? [siteUrl.replace(/^https?:\/\//, '')] : undefined,
              action: {
                label: t('home.map.manage'),
                href: n.slug === 'wix' || isCalendar ? '/settings/connectors' : connectorsPath({ tab: 'tools', target: n.slug ?? '' }),
              },
              link: siteUrl ? { label: t('home.map.viewSite'), href: siteUrl } : undefined,
            };
          }
        case 'channelsIn': {
          const region = t('home.map.channelsIn');
          const phone = recipient?.phone?.trim() || '';
          switch (n.slug) {
            case 'whatsapp': {
              // The number Symphony hears you on, and the groups it sits in (`useWhatsappGroups` already returns
              // only the live ones); without a number, connect first.
              const live = groups;
              return {
                key: n.id,
                region,
                title: n.label,
                meta: phone ? phone : t('home.map.notConnected'),
                text: recent?.text,
                lines: live.length ? [t('home.map.groups', { count: live.length }), ...live.slice(0, 4).map((g) => g.subject)] : undefined,
                action: phone
                  ? { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'whatsapp' }) }
                  : { label: t('home.map.connectAndVerify'), onClick: openPhoneConnect },
                link: phone ? { label: t('home.map.chatOnWhatsapp'), href: WHATSAPP_DIRECT_URL } : undefined,
              };
            }
            case 'email-in':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: me?.email || undefined,
                text: recent?.text,
                action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels' }) },
              };
            case 'sms-in':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: phone ? phone : t('home.map.notConnected'),
                text: recent?.text,
                action: phone
                  ? { label: t('home.map.sendSms'), href: continueOnChannelHref('SMS') ?? undefined }
                  : { label: t('home.map.connectAndVerify'), onClick: openPhoneConnect },
              };
            case 'web':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: me?.email ? t('home.map.signedInAs', { email: me.email }) : t('home.map.thisBrowser'),
                text: t('home.map.thisBrowser'),
                action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'symphony-apps' }) },
              };
            case 'phone-app':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: t('home.map.installed'),
                text: recent?.text,
                action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'symphony-apps' }) },
              };
            default: {
              if (n.slug?.startsWith('coder:')) {
                const id = n.slug.slice('coder:'.length);
                const conn = coderConnections.find((c) => c.id === id);
                // The client, as the Channels screen describes it: platform, when it connected, when it was last
                // used, and how many threads it holds open.
                const last = conn?.lastUsedDate ? t('home.map.lastUsed', { time: shortRelativeTime(conn.lastUsedDate) }) : undefined;
                const since = conn?.connectedDate ? t('home.map.connectedSince', { time: shortRelativeTime(conn.connectedDate) }) : undefined;
                // The meta line is the freshest fact (platform · last used, else when it connected); the
                // rest are lines, never the same fact twice.
                const meta =
                  [conn?.agentPlatform && conn.agentPlatform !== n.label ? conn.agentPlatform : undefined, last].filter(Boolean).join(' · ') ||
                  since ||
                  t('home.map.connected');
                const lines = [
                  meta === since ? undefined : since,
                  conn?.openThreadCount ? t('home.map.openThreads', { count: conn.openThreadCount }) : undefined,
                  conn?.agentUrl ? conn.agentUrl.replace(/^https?:\/\//, '') : undefined,
                ].filter((l): l is string => !!l);
                return {
                  key: n.id,
                  region,
                  title: n.label,
                  meta,
                  text: recent?.text,
                  lines: lines.length ? lines : undefined,
                  action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'ai-coders' }) },
                };
              }
              return {
                key: n.id,
                region,
                title: n.label,
                meta: recent ? timeOf(recent.at) : t('home.map.connected'),
                text: recent?.text,
                action: { label: t('home.map.manage'), href: '/settings/connectors' },
              };
            }
          }
        }
        case 'outreach': {
          const connected = n.connected !== false;
          // Replies waiting on the reader, on this channel: who, and what they last said.
          const due = waiting[n.slug ?? ''] ?? [];
          // One clipped line each: the card is a summary, the thread is the "View message" away.
          const clip = (text: string) => (text.length > 90 ? `${text.slice(0, 89)}…` : text);
          const dueLines = due.slice(0, 3).map((w) => clip([w.name, w.text].filter(Boolean).join(' — ')));
          // Messenger: neither the outreach service nor the Facebook connector can DM a person — say so.
          const messenger = n.slug === 'messenger';
          return {
            key: n.id,
            region: t('home.map.outreach'),
            title: n.label,
            // The Symphony identity that sends on this channel: "{agent} on {channel}".
            meta: connected
              ? due.length
                ? t('home.map.waitingReplies', { count: due.length })
                : t('home.map.sendsAs', { agent: agentName.short, channel: n.label })
              : t('home.map.notConnected'),
            text: messenger && !connected ? t('home.map.messengerUnsupported') : due.length ? undefined : recent?.text,
            lines: dueLines.length ? dueLines : undefined,
            // Both land on the Channels-out section: the sending identity per channel lives there.
            // A live channel: change who sends on it. A coming one has nothing to connect — the action is a
            // VOTE: a Maestro chat that files the wish as feedback (the same channel every wish goes to).
            // A live channel: the last message is the thing to open (its correspondence); changing who sends
            // is the quiet link beside it.
            action: connected
              ? due[0]?.href
                ? { label: t('home.map.viewMessage'), href: due[0].href }
                : recent?.href
                ? { label: t('home.map.viewMessage'), href: recent.href }
                : { label: t('home.map.change'), href: `/settings/connectors/channels-out/${n.slug}` }
              : prefs.wantedChannels.includes(n.slug ?? '') || voted[n.slug ?? '']
              ? { label: t('connectors.channelsOut.wanted'), done: true }
              : { label: t('connectors.channelsOut.want'), onClick: () => wantChannel(n.slug ?? '', n.label) },
            link:
              connected && (due[0]?.href || recent?.href)
                ? { label: t('home.map.change'), href: `/settings/connectors/channels-out/${n.slug}` }
                : undefined,
          };
        }
        default:
          return null;
      }
    },
    [
      world,
      live.lastByNode,
      agentNames,
      agentsById,
      taskState,
      agentName.short,
      brief,
      t,
      timeOf,
      onTimeline,
      connectorsList,
      coderConnections,
      recipient,
      groups,
      me,
      openPhoneConnect,
      site,
      prefs.wantedChannels,
      voted,
      wantChannel,
      waiting,
    ],
  );

  // The pill: while a minimized call owns the shared capsule the island is not here, but the row keeps
  // its height (the same rule the two home headers apply).
  const pillReady = useAgentsPillReady();
  const pillHere = useChipSlotHolds('pill');
  const reserving = pillReady && !pillHere;

  const section = useMemo(() => {
    switch (tab) {
      case 'tasks':
        return <TasksBody filterable />;
      case 'goals':
        return <GoalsBody filterable />;
      case 'routines':
        return <RoutinesBody filterable />;
      case 'files':
        return <AgentWorkspaceFilesBody />;
      case 'apps':
        return <AppsBody />;
      case 'messages':
        return <OutreachTabBody personId={person || null} onOpen={(id) => setPerson(id)} onBack={() => setPerson(null)} />;
      case 'activity':
        return <ActivityBody />;
      default:
        return null;
    }
  }, [tab, person, setPerson]);

  return (
    <div className={`${styles.root}${tab ? ` ${styles.tabbed}` : ''}`} data-testid="map-home">
      {
        <header className={styles.header}>
          <div className={styles.identity}>
            {showIsland && (
              <div className={`${styles.islandRow}${reserving ? ` ${styles.islandRowReserved}` : ''}`}>
                {reserving ? null : (
                  <GenericEventsRouteProvider value="agents-island">
                    <AgentsIslandBadge side="left" />
                  </GenericEventsRouteProvider>
                )}
              </div>
            )}
            <GreetingDate part="greeting" />
            {/* The pill is a LINK to the notifications, and says so on hover — nothing on this screen may be
                an object with no hover and no click (a phone has no hover at all). */}
            {unread > 0 && (
              <Link
                href="/notifications"
                as={`${basePath}/notifications`}
                className={styles.updates}
                title={t('home.map.newUpdatesHint')}
                aria-label={`${t('home.map.newUpdates', { count: unread })} · ${t('home.map.newUpdatesHint')}`}
                onClick={() => logPageClickBi({ page_name: 'map_home', button_name: 'new-updates' })}
              >
                <Badge
                  variant="connected"
                  size="small"
                  startIcon={<Sparkles size={14} aria-hidden />}
                  label={t('home.map.newUpdates', { count: unread })}
                />
              </Link>
            )}
          </div>
          <GreetingDate part="date" />
        </header>
      }

      <div className={`${styles.mapWrap}${narrow ? ` ${styles.mapBand}` : ''}`}>
        {world ? (
          <HomeMap
            world={world}
            flights={live.flights}
            settle={live.settle}
            landOn={landOn}
            maestroName={agentName.short}
            // The phone's home carries the map as a short live band ("peek"): a tap opens the full map.
            compact={!!tab || narrow}
            onGroundClick={() => {
              if (narrow) {
                logPageClickBi({ page_name: 'map_home', button_name: 'map-expand' });
                setExpanded(true);
              } else if (tab) setTab(null);
            }}
            onTimeline={onTimeline}
            onLand={onLand}
            onOpenSpace={onOpenSpace}
            describe={describe}
            briefCard={<MapBriefCard />}
            badges={badges}
            picked={picked}
            timeline={timeline}
          />
        ) : (
          <PageLoader />
        )}
      </div>

      {/* The phone's full map: over the whole screen, with its controls and timeline; back returns home. */}
      {narrow &&
        expanded &&
        world &&
        typeof document !== 'undefined' &&
        // Portalled: the home's column is a size CONTAINER, which also contains fixed positioning — an
        // overlay inside it would be pinned to the pane, under the phone's floating hamburger and launcher.
        createPortal(
          <div className={styles.overlay} role="region" aria-label={t('home.map.aria')}>
            <div className={styles.overlayBar}>
              <TextButton size="small" startIcon={<ArrowLeft size={16} aria-hidden className="dir-flip" />} onClick={() => setExpanded(false)}>
                {t('home.map.closeMap')}
              </TextButton>
            </div>
            <div className={styles.overlayMap}>
              <HomeMap
                world={world}
                flights={live.flights}
                settle={live.settle}
                landOn={landOn}
                maestroName={agentName.short}
                onTimeline={onTimeline}
                onLand={onLand}
                onOpenSpace={onOpenSpace}
                describe={describe}
                briefCard={<MapBriefCard />}
                badges={badges}
                picked={picked}
                timeline={timeline}
              />
            </div>
          </div>,
          document.body,
        )}

      <MapTiles selected={tab} onSelect={onSelectTile} bumped={bumped} inline={!narrow} />

      {section && (
        <section className={styles.section} aria-live="polite">
          <TextButton size="small" startIcon={<ArrowLeft size={16} aria-hidden className="dir-flip" />} onClick={() => setTab(null)}>
            {t('home.map.backToMap')}
          </TextButton>
          {section}
        </section>
      )}

      {/* Wide only: on a phone the floating chat launcher is the one door into the chat — a composer here
          too was two chats at the foot of one screen. */}
      {/* …and not while the chat is docked at the side: that dock IS the composer, and two inputs for one
          chat on one screen was the report. */}
      {!narrow && !chatOpen && (
        <div className={styles.composer}>
          <HomeComposer continueRecent />
        </div>
      )}
    </div>
  );
}
